<template>
  <aside class="academy-sidebar z-20">
    <template v-for="(navGroup, groupIndex) in navigation">
      <nav v-if="checkNotEmpty(navGroup)" :key="groupIndex" class="academy-sidebar__nav-group">
        <template v-for="(navItem, navIndex) in navGroup">
          <nuxt-link
            v-if="!navItem.isHide"
            :key="navIndex"
            :to="navItem.href"
            :title="navItem.label"
            :class="[navItemClasses(navItem.activePathStartsWith), `Components__Sidebar__NavItem-${navItem.label}`]"
            :event="disabledByIcon(navItem.icon)"
          >
            <BaseIconSVG
              v-if="navItem.iconType === 'custom'"
              iconClass="academy-sidebar__nav-icon"
              class="fill-primary-10"
              :name="navItem.icon"
              :width="32"
              :height="32"
              :size="32"
            />
            <span v-else-if="navItem.iconType === 'material-icon'" class="academy-sidebar__nav-icon is-marterial-icon">
              <b-icon size="is-medium" :icon="navItem.icon" />
            </span>
            <span class="academy-sidebar__nav-label">{{ $t(navItem.label) }}</span>
          </nuxt-link>
        </template>
      </nav>
    </template>

    <slot name="rightSideBar"></slot>
  </aside>
</template>

<script>
import some from 'lodash/some'
import cloneDeep from 'lodash/cloneDeep'
import cn from 'classnames'
import { mapGetters, mapActions } from 'vuex'

import BaseIconSVG from '../v2/DataDisplay/BaseIconSVG.vue'
import { USER_ROLES } from '~/global/data/roles'
import { PAGES } from '~/global/data/common'
export default {
  components: {
    BaseIconSVG,
  },
  computed: {
    ...mapGetters({
      academyLearningJourney: 'profile/academyLearningJourney',
      userProfile: 'profile/userProfile',
      isSuper: 'profile/isSuper',
      academyRole: 'profile/academyRole',
      performanceRole: 'profile/performanceRole',
      isAcademyManager: 'profile/isAcademyManager',
      isTrainingManager: 'profile/isTrainingManager',
      isPerformanceManager: 'profile/isPerformanceManager',
      isAdministrator: 'profile/isAdministrator',
    }),
    academyHref() {
      const { academyMinutesPerDay } = this.userProfile
      let academyHref = '/academy/edit-active'
      if (academyMinutesPerDay) {
        academyHref = '/academy'
      }
      return academyHref
    },
    navigation() {
      const isNotManager = !(
        this.isSuper ||
        this.isPerformanceManager ||
        this.isAcademyManager ||
        this.isTrainingManager ||
        this.isAdministrator
      )
      const pages = cloneDeep(PAGES)
      Object.keys(pages).forEach((page) => {
        const pageData = pages[page]
        if (this.isSuper) {
          pageData.isHide = false
        } else {
          switch (page) {
            case 'STAFF':
            case 'SETTING':
            case 'REPORT':
              pageData.isHide = isNotManager
              break
            case 'PERFORMANCE':
              pageData.isHide = !(
                [USER_ROLES.PERFORMANCE_MANAGER, USER_ROLES.PERFORMANCE_STAFF].includes(this.performanceRole) ||
                this.isAdministrator ||
                this.isPerformanceManager
              )
              break
            case 'TRAINING':
              pageData.isHide = !(this.isAcademyManager || this.isTrainingManager || this.isAdministrator)
              break
            case 'ADMIN':
              pageData.isHide = !this.isSuper
              break
            case 'REFLECTION':
              if (!this.orgFeatures?.module_reflection) {
                pageData.isHide = true
              }
              break
            default: {
              pageData.isHide = false
            }
          }
        }
      })
      const sideBarNavigators = [
        [
          // pages.GET_START,
          pages.PERSONAL_PROFILE,
          pages.STAFF,
        ],
        [pages.SCHOOL_PROFILE],
        [
          {
            ...pages.ACADEMY,
            href: this.academyHref,
          },
          pages.PERFORMANCE,
          pages.REFLECTION,
          // pages.COMMUNITY,
          pages.TRAINING,
        ],
        [pages.SETTING, pages.REPORT],
        [pages.ADMIN],
      ]
      return sideBarNavigators
    },
  },
  methods: {
    ...mapActions({
      fetchLearningJourney: 'profile/FETCH_LEARNING_JOURNEY',
    }),
    checkNotEmpty(group) {
      return some(group, (page) => !page.isHide)
    },
    disabledByIcon(icon) {
      if (icon === 'community') {
        return ''
      }
      return 'click'
    },
    navItemClasses(activePathStartsWith) {
      let activeClass = ''
      const { path } = this.$route
      if (path.startsWith(activePathStartsWith)) {
        activeClass = 'academy-sidebar__nav-item--active'
      }
      return cn('academy-sidebar__nav-item', activeClass)
    },
  },
}
</script>

<style lang="scss">
.academy-sidebar {
  position: fixed;
  left: 0;
  top: 60px;
  bottom: 0;
  z-index: 39;

  /* min-width: 220px; */
  width: 240px;
  height: 100%;
  overflow-y: auto;
  padding: 40px 24px;
  background-color: $color-theme-background;
  z-index: 11;
  transition: 0.4s all ease;
  @media screen and (max-width: 1210px) {
    min-width: 80px;
    width: 80px;
    .academy-sidebar__nav-label {
      opacity: 0;
      visibility: hidden;
      width: 0;
      height: 0;
    }
  }
  @media screen and (max-width: 540px) {
    min-width: unset;
    width: 48px;
    padding: 12px 6px;
  }
}

.academy-sidebar__nav-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 34px;
}

.academy-sidebar__nav-item {
  height: 32px;
  margin-bottom: 8px;
  color: $color-text-dark-light;
  font-size: $font-size-16px;
  display: flex;
  align-items: center;

  &:hover {
    .academy-sidebar__nav-label {
      color: $color-bright-sky-blue-two;
    }
  }
}

.academy-sidebar__nav-label {
  transition: transform 0.3s ease;
}

.academy-sidebar__nav-icon {
  margin-right: 12px;
  width: 32px;
  height: 32px;
  &.is-marterial-icon {
    color: $color-bright-sky-blue-two;
  }
  @media screen and (max-width: 1210px) {
    margin-right: 0;
  }
}

.academy-sidebar__nav-item--active {
  font-weight: bold;
  color: $color-bright-sky-blue-two;

  &:hover {
    color: $color-bright-sky-blue-two;
  }
}
</style>
